import * as _ from 'lodash';
// eslint-disable-next-line no-restricted-imports
import moment from 'moment';
import { useState } from 'react';

import { BannerStyle } from '../../../../../typings/enums';
import { parseContentsJSON } from '../../../generic/QuillEditor';
import type { Banner, CreateBannerRequest, UpdateBannerRequest } from '../../../../../typings/api';
import type { TextInputOnChange } from '../../../generic';
import type { QuillInsertOp } from '../../../generic/QuillEditor';

type WithEditorBody<T extends { body: unknown }> = Omit<T, 'body'> & {
  body: {
    contents: QuillInsertOp[];
    length: number;
  };
};

export type NewBanner = WithEditorBody<CreateBannerRequest>;

const hasBody = (ops: QuillInsertOp[]): boolean =>
  ops.some(op => op.insert && op.insert !== '\n');

// When no body was provided, we still have one op that adds a newline because that's how  Quill works, but that creates
// a paragraph. Treat such a body as actually empty.
export const bodyContentsJSON = (banner: NewBanner) =>
  hasBody(banner.body.contents) ? JSON.stringify(banner.body.contents) : '[]';

/**
 * Create a NewBanner object with default values.
 */
export function newBanner(banner: Partial<NewBanner> = {}): NewBanner {
  return {
    title: '',
    body: {
      contents: [],
      length: 0,
    },
    isDismissible: true,
    style: BannerStyle.Low,
    startDate: moment().startOf('day').toISOString(),
    endDate: null,
    ...banner,
  };
}

export type AddModeState = {
  mode: 'add';
  banner: WithEditorBody<CreateBannerRequest>;
};

export type EditModeState = {
  mode: 'edit';
  // The banner object's "body" property is set to an object that holds Delta insert ops and a length. When the rich
  // text editor for the body is initially mounted, this objects contents are used as the editor's initial contents.
  banner: WithEditorBody<UpdateBannerRequest> & Pick<Banner, 'id' | 'isDeactivated'>,
  // In edit mode, the banner's original title is saved so that it's displayed in the modal even as the user changes the
  // title, that is until the banner is saved.
  originalTitle: string;
};

export type State =
  | AddModeState
  | EditModeState;

export type UseAddEditBanner = {
  state: State | null;
  isEdited: boolean;
  onAddNewBanner: () => void;
  onEditBanner: (banner: Banner) => void;
  onDuplicateBanner: (banner: Banner) => void;
  onCloseModal: () => void;
  onTitleChange: TextInputOnChange;
  onBodyChange: (editorState: { contents: QuillInsertOp[]; length: number }) => void;
  onStartDateChange: (startDate: string) => void;
  onEndDateChange: (endDate: string | null) => void;
  onToggleIsDismissible: (isDismissible: boolean) => void;
  onBannerStyleChange: (bannerStyle: BannerStyle) => void;
};

/**
 * Manages state for the add/edit banner modal.
 */
const useAddEditBanner = (): UseAddEditBanner => {
  const [state, setState] = useState<State | null>(null);
  const [isEdited, setIsEdited] = useState<boolean>(false);

  //  Handle setting the value of a field, handling the null case properly.
  const setBanner = <Field extends keyof NewBanner>(field: Field) => (value: NewBanner[Field]) => {
    setState(prevState => {
      if (prevState) {
        return { ...prevState, banner: { ...prevState.banner, [field]: value } } as State;
      }
      return null;
    });
    setIsEdited(true);
  };

  const onAddNewBanner = () =>
    setState({
      mode: 'add',
      banner: newBanner(),
    });

  const onDuplicateBanner = ({ body, ...bannerToDuplicate }: Banner) => {
    setState({
      mode: 'add',
      banner: newBanner({
        ..._.omit(bannerToDuplicate, ['id']),
        title: `${bannerToDuplicate.title} (copy)`,
        body: {
          contents: parseContentsJSON(body),
          // We can use 0 as the default length because it will get updated as soon as the editor's contents are set.
          length: 0,
        },
      }),
    });
    setIsEdited(true);
  };

  const onEditBanner = (banner: Banner) => {
    setState({
      mode: 'edit',
      banner: {
        ...banner,
        body: {
          contents: parseContentsJSON(banner.body),
          // We can use 0 as the default length because it will get updated as soon as the editor's contents are set.
          length: 0,
        },
      },
      originalTitle: banner.title,
    });
  };

  const onCloseModal = () => {
    setState(null);
    setIsEdited(false);
  };

  const onTitleChange = setBanner('title');
  const onBodyChange = setBanner('body');
  const onStartDateChange = setBanner('startDate');
  const onEndDateChange = setBanner('endDate');
  const onToggleIsDismissible = setBanner('isDismissible');
  const onBannerStyleChange = setBanner('style');

  return {
    state,
    isEdited,
    onAddNewBanner,
    onEditBanner,
    onDuplicateBanner,
    onCloseModal,
    onTitleChange,
    onBodyChange,
    onStartDateChange,
    onEndDateChange,
    onToggleIsDismissible,
    onBannerStyleChange,
  };
};

export default useAddEditBanner;
